/**
 * Customer Hooks
 *
 * TanStack Query hooks for customer management operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { reviewRequestKeys } from '../reviewRequest/reviewRequest.hooks.js';
import {
    checkPhoneNumberExistsApi,
    createCustomerApi,
    deleteCustomerApi,
    shopCreatedCustomerDetailApi,
    shopImportedCustomerDetailApi,
    updateCustomerApi,
    updateCustomerImportedApi,
} from './customer.api';

// Query Keys for customer domain
export const customerKeys = {
    all: ['customers'],
    lists: () => [...customerKeys.all, 'list'],
    list: (filters) => [...customerKeys.lists(), { filters }],
    createdDetails: () => [...customerKeys.all, 'created-detail'],
    importedDetails: () => [...customerKeys.all, 'imported-detail'],
    createdDetail: (id) => [...customerKeys.createdDetails(), id],
    shopImportedDetail: (id) => [...customerKeys.importedDetails(), id],
    importedDetail: (id) => [...customerKeys.importedDetails(), id],
};

/**
 * Hook to check if phone number exists
 *
 * This mutation checks if a customer with the given phone number already exists.
 * If found, it returns the customer data which can be used to pre-fill the form.
 *
 * @param {object} options - Mutation options
 * @param {function} options.onSuccess - Success callback with customer data
 * @param {function} options.onError - Error callback
 * @returns {object} Mutation result with mutate function and loading states
 *
 * @example
 * const checkPhoneMutation = useCheckPhoneNumber({
 *   onSuccess: (data) => {
 *     if (data.status) {
 *       // Customer found, pre-fill form
 *       setFormData(data.data);
 *     }
 *   }
 * });
 *
 * checkPhoneMutation.mutate({
 *   phoneNumber: '1234567890',
 *   countryCode: '+1',
 *   phoneNumberCountry: 'country_id'
 * });
 */
export const useCheckPhoneNumber = (options = {}) => {
    const { onSuccess, onError, ...restOptions } = options;

    return useMutation({
        mutationFn: checkPhoneNumberExistsApi,
        onSuccess: (data, variables, context) => {
            console.log('🚀 ~ useCheckPhoneNumber ~ data:', data);
            // Call user's onSuccess if provided
            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },
        onError: (error, variables, context) => {
            console.error('Error checking phone number:', error);
            toast.error('Failed to check phone number');

            // Call user's onError if provided
            if (onError) {
                onError(error, variables, context);
            }
        },
        ...restOptions,
    });
};

/**
 * Hook to create a new customer
 *
 * This mutation creates a new customer with all provided details including
 * contact information, address, and vehicle data.
 *
 * @param {object} options - Mutation options
 * @param {function} options.onSuccess - Success callback with created customer data
 * @param {function} options.onError - Error callback
 * @returns {object} Mutation result with mutate function and loading states
 *
 * @example
 * const createMutation = useCreateCustomer({
 *   onSuccess: (data) => {
 *     toast.success('Customer created successfully');
 *     onClose();
 *   }
 * });
 *
 * createMutation.mutate({
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   phoneNumber: '1234567890',
 *   // ... other fields
 * });
 */
export const useCreateCustomer = (options = {}) => {
    const queryClient = useQueryClient();
    const { onSuccess, onError, ...restOptions } = options;

    return useMutation({
        mutationFn: createCustomerApi,
        onSuccess: (data, variables, context) => {
            // Invalidate customer lists to refetch
            queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
            // Ensure Quick Review customer chips stay fresh across the app
            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'] });
            toast.success('Customer created successfully');

            // Call user's onSuccess if provided
            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },
        onError: (error, variables, context) => {
            console.error('Error creating customer:', error);
            toast.error(error?.response?.data?.message || 'Failed to create customer');

            // Call user's onError if provided
            if (onError) {
                onError(error, variables, context);
            }
        },
        ...restOptions,
    });
};

/**
 * Hook to update an existing customer
 *
 * This mutation updates an existing customer's details. Used when a customer
 * with the same phone number is found and we want to update their information.
 *
 * @param {object} options - Mutation options
 * @param {function} options.onSuccess - Success callback with updated customer data
 * @param {function} options.onError - Error callback
 * @returns {object} Mutation result with mutate function and loading states
 *
 * @example
 * const updateMutation = useUpdateCustomer({
 *   onSuccess: (data) => {
 *     toast.success('Customer updated successfully');
 *     onClose();
 *   }
 * });
 *
 * updateMutation.mutate({
 *   id: 'customer_id',
 *   payload: {
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     // ... other fields
 *   }
 * });
 */
export const useUpdateCustomer = (options = {}) => {
    const queryClient = useQueryClient();
    const { onSuccess, onError, ...restOptions } = options;

    return useMutation({
        mutationFn: ({ id, payload }) => updateCustomerApi(id, payload),
        onSuccess: (data, variables, context) => {
            // Invalidate customer lists and specific customer detail
            queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
            queryClient.invalidateQueries({ queryKey: customerKeys.createdDetail(variables.id) });
            // Ensure Quick Review customer chips stay fresh across the app
            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'] });

            toast.success('Customer updated successfully');

            // Call user's onSuccess if provided
            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },
        onError: (error, variables, context) => {
            console.error('Error updating customer:', error);
            toast.error(error?.response?.data?.message || 'Failed to update customer');

            // Call user's onError if provided
            if (onError) {
                onError(error, variables, context);
            }
        },
        ...restOptions,
    });
};
/**
 * Hook to get created customer detail
 *
 * This query fetches detailed information for a shop-created customer by ID.
 * Used when editing an existing customer to pre-fill the form with their data.
 *
 * @param {string} customerId - The customer ID to fetch details for
 * @param {object} options - Query options
 * @param {function} options.onSuccess - Success callback with customer data
 * @param {function} options.onError - Error callback
 * @param {boolean} options.enabled - Whether the query should run (default: true if customerId exists)
 * @returns {object} Query result with data, isLoading, error, and refetch function
 *
 * @example
 * const { data: customerDetail, isLoading, refetch } = useGetCreatedCustomer(customerId, {
 *   enabled: !!customerId
 * });
 */
export const useGetCreatedCustomer = (customerId, options = {}) => {
    const queryClient = useQueryClient();
    const { onSuccess, onError, ...restOptions } = options;

    return useQuery({
        queryKey: customerKeys.createdDetail(customerId),
        queryFn: () => shopCreatedCustomerDetailApi(customerId),
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            console.error('Error fetching created customer detail:', error);
            if (onError) {
                onError(error);
            }
        },
        ...restOptions,
    });
};

/**
 * Hook to get imported customer detail
 *
 * This query fetches detailed information for a shop-imported customer by ID.
 * Used when editing an existing customer to pre-fill the form with their data.
 *
 * @param {string} customerId - The customer ID to fetch details for
 * @param {object} options - Query options
 * @param {function} options.onSuccess - Success callback with customer data
 * @param {function} options.onError - Error callback
 * @param {boolean} options.enabled - Whether the query should run (default: true if customerId exists)
 * @returns {object} Query result with data, isLoading, error, and refetch function
 *
 */
export const useGetImportedCustomer = (_id, options = {}) => {
    const queryClient = useQueryClient();
    const { onSuccess, onError, ...restOptions } = options;

    return useQuery({
        queryKey: customerKeys.importedDetail(_id),
        queryFn: () => shopImportedCustomerDetailApi(_id),
        onSuccess: (data) => {
            if (onSuccess) {
                onSuccess(data);
            }
        },
        onError: (error) => {
            console.error('Error fetching imported customer detail:', error);
            if (onError) {
                onError(error);
            }
        },
        ...restOptions,
    });
};

/**
 * Hook to update an existing customer
 *
 * This mutation updates an existing customer's details. Used when a customer
 * with the same phone number is found and we want to update their information.
 *
 * @param {object} options - Mutation options
 * @param {function} options.onSuccess - Success callback with updated customer data
 * @param {function} options.onError - Error callback
 * @returns {object} Mutation result with mutate function and loading states
 *
 * @example
 * const updateMutation = useUpdateCustomer({
 *   onSuccess: (data) => {
 *     toast.success('Customer updated successfully');
 *     onClose();
 *   }
 * });
 *
 * updateMutation.mutate({
 *   id: 'customer_id',
 *   payload: {
 *     firstName: 'John',
 *     lastName: 'Doe',
 *     // ... other fields
 *   }
 * });
 */
export const useUpdateCustomerImported = (options = {}) => {
    const queryClient = useQueryClient();
    const { onSuccess, onError, ...restOptions } = options;

    return useMutation({
        mutationFn: ({ payload }) => updateCustomerImportedApi(payload),
        onSuccess: (data, variables, context) => {
            // Invalidate customer lists and specific customer detail
            queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
            queryClient.invalidateQueries({ queryKey: customerKeys.shopImportedDetail() });
            // Ensure Quick Review customer chips stay fresh across the app
            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'] });

            toast.success('Customer information confirmed successfully');

            // Call user's onSuccess if provided
            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },
        onError: (error, variables, context) => {
            console.error('Error updating customer :', error);
            toast.error(error?.response?.data?.message || 'Failed to update customer shop_imported');

            // Call user's onError if provided
            if (onError) {
                onError(error, variables, context);
            }
        },
        ...restOptions,
    });
};

export const useDeleteCustomer = (options = {}) => {
    const queryClient = useQueryClient();
    const { onSuccess, onError, ...restOptions } = options;

    return useMutation({
        mutationFn: (id) => deleteCustomerApi(id),
        onSuccess: (data, variables, context) => {
            // Invalidate customer lists to refetch
            queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
            // Ensure Quick Review customer chips stay fresh across the app
            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'] });
            toast.success(data?.message || 'Customer deleted successfully');

            // Call user's onSuccess if provided
            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },
        onError: (error, variables, context) => {
            console.error('Error deleting customer:', error);
            toast.error(error?.response?.data?.message || 'Failed to delete customer');

            // Call user's onError if provided
            if (onError) {
                onError(error, variables, context);
            }
        },
        ...restOptions,
    });
};
